import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useTracking } from 'lib/babylistHealthAnalytics'
import { UI } from '../../../global'
import { SUBSCRIPTIONS_PATH } from '../../constants'
import ReplacementPartsForm from '../../forms/ReplacementPartsForm'
import { configureView } from '../../utils/ViewConfiguration'
import ReplacementPartsOrderBlocker, {
  ReplacementPartsOrderBlockerProps,
} from './ReplacementPartsOrderBlocker'
import InsuranceVerificationFailed from './InsuranceVerificationFailed/InsuranceVerificationFailed'
import getSubscriptionProducts from '../../requests/getSubscriptionProducts'
import useCheckout from '../../forms/ReplacementPartsForm/useCheckout'
import { verifyOrderInsurance } from '../../requests'
import { insuranceVerificationErrorTypeAdapter } from '../../forms/ReplacementPartsForm/ReplacementPartsForm.utils'
import apiErrorResponseAdapter, {
  Error,
} from '../../utils/apiErrorResponseAdapter'
import { SUBSCRIPTION_ERROR_TYPES } from '../../forms/ReplacementPartsForm/errorTypes'
import { errorThrownEvent } from '../../events/errorThrownEvent'
import useMinWaitTimer from '../../shared/useMinWaitTimer'
import { useDMEStore } from '../../store'

export interface CustomerInsuranceProps {
  id: APIResponse.CustomerInsurance['id']
}
export interface ReplacementPartsViewProps extends UI.LifeCycle {
  heroProducts: APIResponse.HeroProduct[]
  insurance: CustomerInsuranceProps
  subscription: APIResponse.DME.Subscription
  subscriptionError?: APIResponse.DME.SubscriptionError
}

const takeoverErrorType = (
  e: APIResponse.DME.SubscriptionErrorType | null
): ReplacementPartsOrderBlockerProps['error'] | null =>
  e && e !== 'INSURANCE_VERIFICATION_FAILED' ? e : null

const isProductIncompatibilityError = (
  e: ReturnType<typeof apiErrorResponseAdapter>
) =>
  // TODO: Make this less brittle. We shouldn't depend on the human-readable error message,
  // but the API response does not contain any other information about the error.
  e.message.match(/not compatible with your insurance/)

const ReplacementPartsView: UI.ViewComponent<ReplacementPartsViewProps> =
  observer(
    ({
      heroProducts: initialHeroProducts,
      insurance,
      subscription,
      subscriptionError,
      onComponentDidMount,
    }: ReplacementPartsViewProps) => {
      const tracker = useTracking()
      const store = useDMEStore()
      const [error, setError] =
        useState<APIResponse.DME.SubscriptionErrorType | null>(
          subscriptionError?.error || null
        )
      const [formErrorMessage, setFormErrorMessage] = useState('')
      const [customerInsuranceId, setCustomerInsuranceId] = useState<
        APIResponse.CustomerInsurance['id']
      >(insurance.id)
      const { data: heroProducts = [], refetch: refetchProducts } = useQuery(
        'subscriptionHeroProducts',
        getSubscriptionProducts.bind(null, subscription.id),
        {
          refetchOnWindowFocus: false,
          initialData: initialHeroProducts,
        }
      )
      const [verifyInsurance, { isLoading: isInsuranceLoading }] = useMutation(
        verifyOrderInsurance,
        {
          onSuccess: () => {
            timedOverlay.wait(() => {
              checkout()
            })
          },
          onError: (e?: APIResponse.StandardError) => {
            timedOverlay.wait(() => {
              const adaptedError = insuranceVerificationErrorTypeAdapter(e)
              setError(adaptedError)
            })
          },
        }
      )
      const timedOverlay = useMinWaitTimer(
        5000,
        () => {
          store.ui.globalComponents.Overlay.show('insuranceVerification')
        },
        () => {
          store.ui.globalComponents.Overlay.close('insuranceVerification')
        }
      )

      const checkoutErrorHandler = (e: Error) => {
        const adaptedError = apiErrorResponseAdapter(e)
        errorThrownEvent(tracker)({
          error: adaptedError.message,
        })

        if (SUBSCRIPTION_ERROR_TYPES.includes(adaptedError.message)) {
          setError(
            adaptedError.message as APIResponse.DME.SubscriptionErrorType
          )
        } else {
          if (isProductIncompatibilityError(adaptedError)) {
            refetchProducts()
          }
          setFormErrorMessage(adaptedError.message)
        }
      }

      const {
        checkout,
        setCheckoutParams,
        isLoading: isCheckoutLoading,
        isNavigating,
      } = useCheckout(
        { customerInsuranceId, subscriptionId: subscription.id },
        checkoutErrorHandler
      )

      useEffect(() => {
        onComponentDidMount()
      }, [])

      const handleError = (e: APIResponse.DME.SubscriptionErrorType) => {
        setError(e)
      }

      const handleInsuranceFormSuccess = ({
        id,
      }: APIResponse.CustomerInsurance) => {
        refetchProducts()
        setCustomerInsuranceId(id)
        setError(null)
        checkout()
      }

      const handleCheckoutFormSubmit = (params: {
        productId: number
        aobAcknowledged: boolean
      }) => {
        // The price of the selected product isn't particularly valuable for the UX logic,
        // but we need to pass it to checkout for event tracking purposes.
        // See `addToCartEvent` within the `useCheckout` hook.
        const selectedProduct = heroProducts
          .map(
            (heroProduct) =>
              heroProduct.products || heroProduct.availableProducts
          )
          .flat()
          .find((p) => p.id === params.productId)
        const productPrice = Number(selectedProduct?.price || 0)

        setCheckoutParams({
          productId: params.productId,
          productPrice,
          aobAcknowledged: params.aobAcknowledged,
        })
        timedOverlay.start()
        verifyInsurance({ customerInsuranceId })
      }

      if (error === 'INSURANCE_VERIFICATION_FAILED') {
        return (
          <InsuranceVerificationFailed
            customerId={subscription.dmeCustomerId}
            onSuccess={handleInsuranceFormSuccess}
          />
        )
      }

      const takeoverError = takeoverErrorType(error)
      if (takeoverError) {
        return (
          <ReplacementPartsOrderBlocker
            error={takeoverError}
            subscription={subscription}
          />
        )
      }

      return (
        <ReplacementPartsForm
          formErrorMessage={formErrorMessage}
          heroProducts={heroProducts}
          loading={isInsuranceLoading || isCheckoutLoading || isNavigating}
          onError={handleError}
          onSubmit={handleCheckoutFormSubmit}
        />
      )
    }
  )

export default configureView(ReplacementPartsView)({
  pathname: SUBSCRIPTIONS_PATH,
})
